import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import { Json } from '../type';
import { pick } from 'remeda';
const useLocalMemoize = (fn, cacheKey) => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Map();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const cache = useRef(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      cache.current.clear();
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== cacheKey || $[4] !== fn) {
    t3 = (...t4) => {
      const args = t4;
      const key = cacheKey ? cacheKey(args) : JSON.stringify(args);
      if (cache.current.has(key)) {
        return cache.current.get(key) as ReturnType<T>;
      }
      const result = fn(...args) as ReturnType<T>;
      cache.current.set(key, result);
      return result;
    };
    $[3] = cacheKey;
    $[4] = fn;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const memoizedFn = t3;
  return memoizedFn;
};
export default useLocalMemoize;
type FontProperties = Partial<Pick<CSSStyleDeclaration, 'fontFamily' | 'fontWeight' | 'fontSize'>>;
const textWidth = (text: string, {
  ref,
  ...p
}: {
  fallBackEmWidth?: number;
  /*css font string*/
  font?: FontProperties;
  ref: React.RefObject<any>;
}) => {
  const fallback = text.length * (p.fallBackEmWidth ?? 16);
  if (typeof document == 'undefined') return fallback;
  const el = ref.current ?? document.body;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return fallback;
  const f = {
    ...pick(window.getComputedStyle(el), ['fontFamily', 'fontWeight', 'fontSize']),
    ...p.font
  };
  ctx.font = `${f.fontWeight} ${f.fontSize} ${f.fontFamily}`;
  return ctx.measureText(text).width;
};
export const useEmSize = <T extends HTMLElement = any,>(options?: {
  dependencies?: Json[];
}) => {
  const {
    dependencies
  } = options ?? {};
  const [emSize, setEmSize] = useState<number>(16);
  const ref = useRef<T>(null);
  const updateEmSize = () => {
    if (ref.current) {
      const fontSize = window.getComputedStyle(ref.current).fontSize;
      setEmSize(parseFloat(fontSize));
    }
  };

  // initial size & updates to dependencies provided
  useEffect(() => updateEmSize(), [ref.current, dependencies ? JSON.stringify(dependencies) : undefined]);

  // style and resize changes
  const mutationObserver = useRef<MutationObserver | null>(null);
  const handleResize = () => updateEmSize();
  useEffect(() => {
    if (ref.current) {
      mutationObserver.current = new MutationObserver(() => {
        updateEmSize();
      });
      mutationObserver.current.observe(ref.current, {
        attributes: true,
        //observe attributes change
        attributeFilter: ['style'] //only observe style attribute change
      });
      window.addEventListener('resize', handleResize);
    }
    return () => {
      if (mutationObserver.current) {
        mutationObserver.current.disconnect();
        mutationObserver.current = null;
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [ref]);
  const cachedTextWidth = useLocalMemoize(textWidth, ([text, {
    font
  }]) => JSON.stringify({
    text,
    font,
    emSize
  }));
  const getTextWidth = useCallback((text_0: string, p?: {
    font?: FontProperties;
  }) => cachedTextWidth(text_0, {
    ref,
    font: p?.font
  }), [emSize, ref.current]);
  return {
    emSize,
    ref,
    getTextWidth
  };
};